import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Minus } from 'lucide-react';

export default function ChatPopup({ user, conversation, onClose, onMinimize, isMinimized }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    if (conversation && !isMinimized) {
      loadMessages();
      markAsRead();

      // Poll for new messages every 2 seconds
      const interval = setInterval(() => {
        loadMessages();
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [conversation, isMinimized]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/messages/${user.uid}/${conversation.otherUserId}`);
      const data = await response.json();

      console.log('Loaded messages:', data);

      if (data.success) {
        const newMessages = data.messages || [];

        // Only update if messages have changed to avoid unnecessary re-renders
        if (JSON.stringify(newMessages) !== JSON.stringify(messages)) {
          console.log('Updating messages. Old count:', messages.length, 'New count:', newMessages.length);
          setMessages(newMessages);

          // Mark as read when new messages arrive
          if (newMessages.length > messages.length) {
            markAsRead();
          }
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading messages:', error);
      setLoading(false);
    }
  };

  const markAsRead = async () => {
    try {
      await fetch(`${API_URL}/api/messages/read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          otherUserId: conversation.otherUserId
        })
      });
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const messageContent = newMessage.trim();
    console.log('Sending message:', messageContent);

    try {
      const response = await fetch(`${API_URL}/api/messages/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromUserId: user.uid,
          toUserId: conversation.otherUserId,
          content: messageContent
        })
      });

      const data = await response.json();
      console.log('Send message response:', data);

      if (data.success) {
        setNewMessage('');
        // Immediately reload messages to show the sent message
        await loadMessages();
      } else {
        console.error('Failed to send message:', data);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';

    if (timestamp._seconds !== undefined) {
      const date = new Date(timestamp._seconds * 1000);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;

      return date.toLocaleDateString();
    }

    return timestamp;
  };

  if (isMinimized) {
    return (
      <div className="fixed bottom-0 right-24 w-80 bg-white rounded-t-lg shadow-2xl border border-gray-200 z-50">
        <div
          onClick={onMinimize}
          className="p-3 bg-blue-600 text-white rounded-t-lg flex items-center justify-between cursor-pointer hover:bg-blue-700"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center bg-white/20">
              <span className="text-lg">{conversation.otherUserAvatar}</span>
            </div>
            <div>
              <h3 className="font-semibold text-sm">{conversation.otherUserName}</h3>
            </div>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="p-1 hover:bg-white/20 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-0 right-24 w-96 bg-white rounded-t-lg shadow-2xl border border-gray-200 z-50" style={{ height: '500px' }}>
      {/* Header */}
      <div className="p-3 bg-blue-600 text-white rounded-t-lg flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center bg-white/20">
            <span className="text-lg">{conversation.otherUserAvatar}</span>
          </div>
          <div>
            <h3 className="font-semibold text-sm">{conversation.otherUserName}</h3>
            <p className="text-xs text-blue-100">{conversation.otherUserUsername}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onMinimize}
            className="p-1 hover:bg-white/20 rounded"
          >
            <Minus className="w-4 h-4" />
          </button>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/20 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ height: 'calc(100% - 120px)' }}>
        {loading ? (
          <div className="text-center text-gray-500 text-sm">Loading...</div>
        ) : messages.length === 0 ? (
          <div className="text-center text-gray-500 text-sm py-8">
            No messages yet. Start the conversation!
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.fromUserId === user.uid ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-xs px-3 py-2 rounded-lg ${
                  msg.fromUserId === user.uid
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-900'
                }`}
              >
                <p className="text-sm break-words">{msg.content}</p>
                <span className={`text-xs mt-1 block ${
                  msg.fromUserId === user.uid ? 'text-blue-100' : 'text-gray-500'
                }`}>
                  {formatTime(msg.createdAt)}
                </span>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSendMessage} className="p-3 border-t border-gray-200 bg-gray-50">
        <div className="flex gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            type="submit"
            disabled={!newMessage.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </form>
    </div>
  );
}
